import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { hideConfirmationModal } from '../../actions/App';
import {
  fetchFeaturedCollections,
  refreshCollections,
} from '../../actions/Collections';

import { APIgetCollectionsSummary } from '../../api/Collections';

import { isArchive } from '../../helpers/App';

import ProcessingProgress from '../ProcessingProgress';
import ProcessingSpinner from '../ProcessingSpinner';
import ErrorBoundary from '../ErrorBoundary';

const RemoveCollectionModal = ({
  collections,
  handleClose,
  progress,
  backendActions,
  progressFilesUuid,
  progressCollectionsUuid,
  mode,
  onConfirm,
}) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [removeFilesToggle, setRemoveFilesToggle] = useState(false);
  const [summary, setSummary] = useState({ files: 0, collections: 0 });
  const [error, setError] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm(removeFilesToggle);
      handleClose();
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFilesChange = () => {
    setRemoveFilesToggle(!removeFilesToggle);
  };

  const getProgress = () => {
    if (progressFilesUuid) {
      return backendActions[`progress_${progressFilesUuid}`] || { total: 0, done: 0 };
    }

    if (progressCollectionsUuid) {
      return backendActions[`progress_${progressCollectionsUuid}`] || { total: 0, done: 0 };
    }

    return progress;
  };

  useEffect(() => {
    const fetchCollectionsSummary = async () => {
      setLoading(true);
      try {
        const response = await APIgetCollectionsSummary({
          collectionUuidList: collections.map(c => c.uuid),
          trashed: true,
        });

        if (response.status === 200) {
          setSummary(response.data);
        }
      } catch (_) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionsSummary();
  }, [collections]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Are you sure?' })}
      </ModalHeader>
      <ModalBody>
        {mode === 'permanent' ? (
          <>
            <p className="warning">
              <i className="far fa-triangle-exclamation fa-2x" />
              {intl.formatMessage({
                defaultMessage: 'You are permanently deleting {collectionsCount} {collectionsCount, plural, one {Collection} other {Collections}}.',
              }, {
                collectionsCount: summary.collections || collections.length,
              })}
            </p>
            <p>
              {intl.formatMessage({
                defaultMessage: 'If you delete {collectionsCount, plural, one {this Collection it} other {these Collections they}} will be deleted permanently from your workspace - this can not be undone!',
              }, {
                collectionsCount: summary.collections || collections.length,
              })}
            </p>
          </>
        ) : (
          <>
            <p className="warning">
              {intl.formatMessage({
                defaultMessage: 'You are deleting {collectionsCount} {collectionsCount, plural, one {Collection} other {Collections}}.',
              }, {
                collectionsCount: summary.collections || collections.length,
              })}
            </p>
            <p>
              {intl.formatMessage({
                defaultMessage: 'All Collections inside {collectionsCount, plural, one {this Collection} other {these Collections}} will be also deleted!',
              }, {
                collectionsCount: summary.collections || collections.length,
              })}
              <br />
              {intl.formatMessage({ defaultMessage: 'Files and Collections can be restored from the trash.' })}
              <br />
            </p>
          </>
        )}
        {(summary?.files > 0 || summary?.collections > 0) && (
          <span className="summary">
            {intl.formatMessage({
              defaultMessage: `<strong>{collectionsCount}</strong>
                {collectionsCount, plural, one {Collection} other {Collections}} and
                <strong>{filesCount}</strong> {filesCount, plural, one {file} other {files}} can be deleted`,
            }, {
              collectionsCount: summary.collections,
              filesCount: summary.files,
              strong: (msg) => <strong>{msg}</strong>,
            })}
          </span>
        )}
        {summary?.files > 0 && (
          <FormGroup>
            <Label for="removeFiles" className="switch d-flex mt-3">
              <div className="switch-control input-inner">
                <Input
                  type="checkbox"
                  id="removeFiles"
                  className="input-check check-s form-check-input no-margin"
                  value={true}
                  disabled={loading}
                  onClick={handleRemoveFilesChange}
                />
                <i className="mr-2" />
              </div>
              <div className="switch-details">
                <span>{intl.formatMessage({ defaultMessage: 'Also delete files' })}</span>
              </div>
            </Label>
          </FormGroup>
        )}
        {removeFilesToggle && (
          <div className="modal-warning">
            <i className="far fa-triangle-exclamation fa-2x" />
            <span>
              {intl.formatMessage({
                defaultMessage: `Remember, one file can live in multiple Collections.
                  These files will be deleted in all Collections.`,
              })}
            </span>
          </div>
        )}
        {error && (
          <p className="error">
            {intl.formatMessage({
              defaultMessage: 'Something went wrong. Please try again or contact us.',
            })}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={loading}
            onClick={handleConfirm}
          >
            <ProcessingSpinner show={loading} />
            {loading ? 'Processing...' : 'Delete'}
            <ProcessingProgress
              show={loading}
              progress={getProgress()}
            />
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  settings: state.App.settings,
  progress: state.App.progress,
  confirmationModal: state.App.confirmationModal,
  confirmationModalParams: state.App.confirmationModalParams,
  backendActions: state.App.backendActions,
});

const mapDispatchToProps = dispatch => ({
  loadFeaturedCollections: () => {
    if (isArchive()) {
      dispatch(fetchFeaturedCollections({ archived: true }));
    } else {
      dispatch(fetchFeaturedCollections());
    }
  },
  reloadCollectionList: () => {
    dispatch(refreshCollections());
  },
  closeConfirmationModal: () => {
    dispatch(hideConfirmationModal());
  },
});

RemoveCollectionModal.propTypes = {
  progress: PropTypes.object.isRequired,
  backendActions: PropTypes.object.isRequired,
  collections: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  progressFilesUuid: PropTypes.string,
  progressCollectionsUuid: PropTypes.string,
  mode: PropTypes.string,
};

RemoveCollectionModal.defaultProps = {
  progressFilesUuid: null,
  progressCollectionsUuid: null,
  mode: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(RemoveCollectionModal);
